/**
 * Shared Tasker service config — hoisted out of taskerService.ts +
 * taskerAttachments.ts so all Tasker callers speak the same base URL and
 * auth-flag conventions.
 *
 * The base URL is env-driven: `window.config.REACT_APP_APIGATEWAY` per the
 * runtime config loaded by `public/config.js`, with the Tasker path prefix
 * appended. Prod builds automatically point at the prod gateway; beta at
 * staging; local dev at whatever `public/config.js` carries.
 *
 * This module intentionally does not `import` from `./taskerAuth` — that
 * would form a cycle (taskerAuth already imports `taskerBaseUrl`). The
 * `ensureTaskerAuth` helper uses a dynamic import to defer the resolution.
 */

const TASKER_PATH_PREFIX = "/core/tasker/api/v1";

export function taskerBaseUrl(): string {
  const gw =
    (typeof window !== "undefined" &&
      (window as unknown as { config?: { REACT_APP_APIGATEWAY?: string } })
        .config?.REACT_APP_APIGATEWAY) ||
    "";
  return `${gw}${TASKER_PATH_PREFIX}`;
}

/**
 * `'vendor'` → OTP-captured JWT (via `useVendorToken` axios flag).
 * `'reviewer'` → Bouncer→Tasker exchanged JWT (via `useReviewerToken`
 * axios flag); first use triggers `ensureReviewerToken()` synchronously.
 */
export type TaskerAuthMode = "vendor" | "reviewer";

export function taskerCfg(mode: TaskerAuthMode) {
  return mode === "reviewer"
    ? { useReviewerToken: true, baseURL: taskerBaseUrl() }
    : { useVendorToken: true, baseURL: taskerBaseUrl() };
}

export async function ensureTaskerAuth(mode: TaskerAuthMode): Promise<void> {
  if (mode === "reviewer") {
    const { ensureReviewerToken } = await import("./taskerAuth");
    await ensureReviewerToken();
  }
}
